'use client';

import { useRef, useCallback } from 'react';

interface SwipeToDismissOptions {
  /** Which direction swipe dismisses: 'left' = swipe left to close, 'right' = swipe right to close */
  direction: 'left' | 'right';
  /** Distance threshold in px to trigger dismiss (default: 80) */
  threshold?: number;
  /** Velocity threshold in px/ms (default: 0.3) */
  velocityThreshold?: number;
  /** Called when swipe exceeds threshold */
  onDismiss: () => void;
  /** Enable only on mobile (default: true) */
  mobileOnly?: boolean;
}

/**
 * Hook for swipe-to-dismiss on drawers/panels.
 * Returns touch event handlers + a ref for tracking swipe offset via CSS variable.
 */
export function useSwipeToDismiss({
  direction,
  threshold = 80,
  velocityThreshold = 0.3,
  onDismiss,
  mobileOnly = true,
}: SwipeToDismissOptions) {
  const touchRef = useRef<{ x: number; y: number; time: number; locked: boolean } | null>(null);
  const elRef = useRef<HTMLElement | null>(null);

  const onTouchStart = useCallback((e: React.TouchEvent) => {
    if (mobileOnly && window.innerWidth >= 768) return;
    touchRef.current = {
      x: e.touches[0].clientX,
      y: e.touches[0].clientY,
      time: Date.now(),
      locked: false,
    };
    // Remove transition during drag
    if (elRef.current) {
      elRef.current.style.transition = 'none';
    }
  }, [mobileOnly]);

  const onTouchMove = useCallback((e: React.TouchEvent) => {
    if (!touchRef.current) return;
    const dx = e.touches[0].clientX - touchRef.current.x;
    const dy = e.touches[0].clientY - touchRef.current.y;

    // Lock direction on first significant movement
    if (!touchRef.current.locked) {
      if (Math.abs(dx) < 5 && Math.abs(dy) < 5) return;
      // If vertical movement is dominant, cancel swipe tracking
      if (Math.abs(dy) > Math.abs(dx)) {
        touchRef.current = null;
        return;
      }
      touchRef.current.locked = true;
    }

    // Only allow movement in the dismiss direction
    const validSwipe = direction === 'left' ? dx < 0 : dx > 0;
    if (!validSwipe) {
      if (elRef.current) elRef.current.style.transform = '';
      return;
    }

    e.preventDefault();
    if (elRef.current) {
      elRef.current.style.transform = `translateX(${dx}px)`;
    }
  }, [direction]);

  const onTouchEnd = useCallback(() => {
    if (!touchRef.current || !elRef.current) {
      touchRef.current = null;
      return;
    }

    const el = elRef.current;
    // Read current transform to get final dx
    const matrix = new DOMMatrix(getComputedStyle(el).transform);
    const finalDx = matrix.m41; // translateX value

    const elapsed = Date.now() - touchRef.current.time;
    const velocity = Math.abs(finalDx) / elapsed;
    const shouldDismiss = Math.abs(finalDx) > threshold || velocity > velocityThreshold;

    // Restore transition
    el.style.transition = '';

    if (shouldDismiss) {
      // Animate out fully then dismiss
      const outX = direction === 'left' ? '-100%' : '100%';
      el.style.transform = `translateX(${outX})`;
      setTimeout(() => {
        el.style.transform = '';
        onDismiss();
      }, 200);
    } else {
      // Snap back
      el.style.transform = '';
    }

    touchRef.current = null;
  }, [direction, threshold, velocityThreshold, onDismiss]);

  return {
    /** Attach to the swipeable element via ref callback */
    ref: elRef,
    handlers: {
      onTouchStart,
      onTouchMove,
      onTouchEnd,
    },
  };
}
